import { useCallback, useEffect, useState } from "react";
import { Inbox, Check, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, Money } from "@/components/common";
import DateInput from "@/components/DateInput";
import ReasonDialog from "@/components/ReasonDialog";
import { salesVat } from "@/pages/SalesEntry";
import { api, formatErr } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtMoney } from "@/lib/format";

export default function SalesSubmissions({ clientId, rate, onApproved }) {
  const [rows, setRows] = useState([]);
  const [appr, setAppr] = useState(null);
  const [rej, setRej] = useState(null);
  const load = useCallback(() => api.get("/sales-submissions", { params: { client_id: clientId } }).then(({ data }) => setRows(data)), [clientId]);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  const { vat } = salesVat(appr?.total_sales, rate);

  const approve = async () => {
    try {
      const { data } = await api.post(`/sales-submissions/${appr.id}/approve`, { ...appr, total_sales: Number(appr.total_sales) });
      toast.success(`Approved as ${data.record_no}`); setAppr(null); load(); onApproved?.(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="panel mb-5 border-amber-200 fade-up" data-testid="sales-submissions-panel">
      <div className="px-4 py-3 border-b border-amber-200 bg-amber-50/60 flex items-center gap-2 text-sm font-medium text-amber-900">
        <Inbox className="w-4 h-4" />Client submissions awaiting approval <span className="num bg-amber-600 text-white text-[11px] rounded-full px-2" data-testid="sales-submissions-count">{rows.length}</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {rows.map((r, i) => (
          <li key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-4" data-testid={`sales-submission-${i}`}>
            <div className="num text-xs text-slate-500 w-24">{r.submission_no}</div>
            <div className="num text-sm">{fmtDate(r.period_from)} – {fmtDate(r.period_to)}</div>
            <div className="text-sm">Total <Money value={r.total_sales} strong /> · VAT <Money value={r.vat_amount} /></div>
            <div className="text-[11px] text-slate-400 num">{fmtDateTime(r.created_at)}{r.note ? ` · ${r.note}` : ""}</div>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => setRej(r)} data-testid={`sales-submission-reject-${i}`}><Ban className="w-3.5 h-3.5 mr-1" />Reject</Button>
              <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={() => setAppr({ id: r.id, no: r.submission_no, period_from: r.period_from, period_to: r.period_to, total_sales: String(r.total_sales) })} data-testid={`sales-submission-approve-${i}`}><Check className="w-3.5 h-3.5 mr-1" />Review & approve</Button>
            </div>
          </li>
        ))}
      </ul>
      <Dialog open={!!appr} onOpenChange={(o) => !o && setAppr(null)}>
        <DialogContent className="bg-white" data-testid="sales-approve-dialog">
          <DialogHeader><DialogTitle>Approve {appr?.no}</DialogTitle><DialogDescription>Adjust if needed — any change is logged. A sales entry is created on approval.</DialogDescription></DialogHeader>
          {appr && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="From date"><DateInput value={appr.period_from} onChange={(v) => setAppr({ ...appr, period_from: v })} testid="approve-from" /></Field>
              <Field label="To date"><DateInput value={appr.period_to} onChange={(v) => setAppr({ ...appr, period_to: v })} testid="approve-to" /></Field>
              <Field label="Total sales (incl. VAT)"><Input type="number" value={appr.total_sales} onChange={(e) => setAppr({ ...appr, total_sales: e.target.value })} className="num text-right" data-testid="approve-total" /></Field>
              <Field label={`VAT (${rate}%)`}><div className="h-10 px-3 flex items-center justify-end rounded-md bg-amber-50 border border-amber-200 num">{fmtMoney(vat)}</div></Field>
            </div>
          )}
          <DialogFooter><Button disabled={!(Number(appr?.total_sales) > 0)} onClick={approve} className="bg-emerald-800 hover:bg-emerald-900" data-testid="approve-submit">Approve & create entry</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ReasonDialog open={!!rej} onOpenChange={() => setRej(null)} title="Reject submission?" destructive confirmLabel="Reject" testid="reject-submission-dialog"
        onConfirm={async (reason) => { try { await api.post(`/sales-submissions/${rej.id}/reject`, { reason }); toast.success("Rejected"); setRej(null); load(); } catch (e) { toast.error(formatErr(e)); } }} />
    </div>
  );
}
